import Spacer from '@berlitz/spacer'
import AvailabilityPreviewWrapper from '@components/AvailabilityPreviewWrapper'
import MenuTab, { MenuTabItem } from '@components/MenuTab'
import RoleBasedView from '@components/RoleBasedView'
import Scroller from '@components/Scroller'
import { InstructorSearchResultInfo } from '@interfaces/Instructor'
import { MenuTabBox } from '@layouts/LearningPath/DetailTabs/style'
import { useRouter } from 'next/router'
import React, { useContext, useEffect, useState } from 'react'
import Skeleton from 'react-loading-skeleton'
import { Col, Row } from 'react-styled-flexboxgrid'
import menu from '../Availability/menu'
import { BlockedProvider, FaveProvider, FMIStudentProfileContext } from '../context'
import { Tab } from '../ManageInstructorView/style'
import AboutMe from './AboutMe'
import AverageRating from './AverageRating'
import Header from './Header'
import Info from './Info'
import { IPInstructorProfileLayout } from './interface'
import { Contents, ProfileAvailability, RightInfo, Wrapper } from './style'
import { useFMIBulkForm } from '../BulkForm/Form/useFMIBulkForm'

const InstructorProfileLayout: React.FC<IPInstructorProfileLayout> = ({ loading, instructors = [] }) => {
  const router = useRouter()
  const { userProfile: profile, baseRoute, getRouteParams } = useContext(FMIStudentProfileContext)
  const { isBulkOnlyFeature } = useFMIBulkForm()
  const [programId, setProgramId] = useState('')
  const [faveInstructors, setFaveInstructors] = useState<InstructorSearchResultInfo[]>()
  const [blockedInstructors, setBlockedInstructors] = useState<InstructorSearchResultInfo[]>()
  const [isBlockedInstructor, setIsBlockedInstructor] = useState<boolean | undefined>(false)
  const [currInstructor, setCurrInstructor] = useState<InstructorSearchResultInfo | undefined>(undefined)
  const [at, setAt] = useState(1)
  const InstructorRating = (currInstructor?.Rating || 0) / 2
  const mappedData = instructors.map((item) => item.instructorInfo)

  useEffect(() => {
    const rawInstructor = mappedData.find(({ InstructorId }) => InstructorId === router.query.instructorId)
    const cleanData = mappedData.filter((item) => !item.IsBlocked)
    setProgramId((router.query.programId as string) || '')
    setFaveInstructors(cleanData?.filter((instructor) => instructor.IsPreferred))
    setCurrInstructor(
      rawInstructor?.IsBlocked
        ? rawInstructor
        : cleanData.find(({ InstructorId }) => InstructorId === router.query.instructorId)
    )
    setBlockedInstructors(mappedData.filter((item) => item.IsBlocked && !item.Name?.match(/^#Test/)))
    setIsBlockedInstructor(rawInstructor?.IsBlocked)
  }, [loading, instructors])

  return (
    <FaveProvider
      studentId={profile.SFId}
      programId={programId}
      faveInstructors={faveInstructors || []}
      rawInstructors={mappedData}
    >
      <BlockedProvider
        studentId={profile.SFId}
        programId={programId}
        blockedInstructors={blockedInstructors || []}
        rawInstructors={mappedData}
      >
        <Wrapper>
          <Header
            loading={loading}
            instructors={instructors}
            instructor={currInstructor}
            onChange={(instructorId) => {
              setCurrInstructor(
                instructors.find(({ instructorInfo }) => instructorInfo?.InstructorId === instructorId)?.instructorInfo
              )
              router.push({
                pathname: `${baseRoute}/profile`,
                query: {
                  ...router.query,
                  instructorId,
                },
              })
            }}
          />

          <Spacer size="lg" />

          <Scroller>
            <MenuTabBox>
              <MenuTab activeTab={at}>
                {(isBulkOnlyFeature || isBlockedInstructor ? [menu[0]] : menu).map((item) => (
                  <MenuTabItem smallWidth key={item.tab}>
                    {loading ? (
                      <Skeleton width="100px" height="18px" />
                    ) : (
                      <Tab
                        onClick={() => {
                          if (item.tab === 2 && !isBulkOnlyFeature) {
                            router.push(`${baseRoute}/availability?${getRouteParams()}`)
                          }
                          setAt(item.tab)
                        }}
                      >
                        {item.child}
                      </Tab>
                    )}
                  </MenuTabItem>
                ))}
              </MenuTab>
            </MenuTabBox>
          </Scroller>

          <Spacer size="lg" />

          <Contents>
            <Info
              loading={loading}
              instructor={currInstructor}
              updateBlockedInstructors={(action, instructors) => {
                const IsBlocked = action == 'block'
                setBlockedInstructors(
                  IsBlocked
                    ? [...(blockedInstructors || []), ...instructors]
                    : blockedInstructors?.filter(
                        (i) => !instructors.some((instructor) => instructor.InstructorId === i.InstructorId)
                      )
                )
                setIsBlockedInstructor(IsBlocked)
                setCurrInstructor({
                  ...currInstructor,
                  IsBlocked,
                })
              }}
            />

            <RightInfo>
              <Row>
                <Col className="video" xs={12} md={12} lg={7}>
                  <AboutMe loading={loading} instructor={currInstructor} />
                  <Spacer size="xl" />

                  <RoleBasedView configId="c::scheduling::instructors::averagerating">
                    <div className="star-rating-wrapper">
                      <AverageRating loading={loading} rating={InstructorRating} />
                    </div>
                  </RoleBasedView>
                </Col>

                <Col className="calendar" xs={12} md={12} lg={5}>
                  <ProfileAvailability>
                    <Spacer size="xxxs" />
                    <Spacer size="sm" />
                    <AvailabilityPreviewWrapper
                      instructor={currInstructor?.InstructorId || (router.query.instructorId as string)}
                      loading={loading!}
                    />
                    <Spacer size="sm" />
                    <Spacer size="xxxs" />
                  </ProfileAvailability>
                  <RoleBasedView configId="c::scheduling::instructors::averagerating">
                    <>
                      <Spacer size="sm" />
                      <div className="star-rating-wrapper">
                        <AverageRating loading={loading} rating={InstructorRating} />
                      </div>
                    </>
                  </RoleBasedView>
                </Col>
              </Row>
            </RightInfo>
          </Contents>

          <Spacer size="xxxl" />
        </Wrapper>
      </BlockedProvider>
    </FaveProvider>
  )
}

export default InstructorProfileLayout
